// src/components/Dashboard/StatusFilterChips.tsx
//
// Shared status-chip filter row for the Outsourcing dashboards (EE-238).
// Both the Inquiry dashboard (OutsourcingManagementUI) and the Requisition
// dashboard (RequisitionFilterBar) render the same control, so the sizing,
// spacing and active/hover colour rules live here once instead of being
// duplicated with slightly different values on each page.
//
// Generic over the status enum: pass the enum's values plus its colour map.
// `StatusColors` is defined here rather than in either page's constants file
// so both colour maps can share one shape without importing across features.
import { CSSProperties } from "react";
import { Button } from "@mui/material";
import styles from "./StatusFilterChips.module.css";

export type StatusColors = {
  border: string;
  color: string;
  background: string;
  // The filter chip's fill when selected. Only needed where `color` is not the
  // status hue itself - a solid token (DONE) carries white text there.
  accent?: string;
};

/**
 * The chip colours the two Outsourcing workflows agree on. An Inquiry and the
 * Requisition that raised it move through the same six stages under different
 * enum names, and the two colour maps wrote the same six triples out twice -
 * so a recolour touched one dashboard and left the other behind. Statuses only
 * one flow has (AWAITING_RESPONSE, SUBMITTED, AWAITING_SUPPLIER) stay in that
 * flow's own map.
 */
export const STATUS_PALETTE: Record<
  | "DRAFT"
  | "APPROVED"
  | "ORDERED"
  | "PICKUP_DELIVERY"
  | "READY_TO_USE"
  | "DONE"
  | "CLOSED",
  StatusColors
> = {
  DRAFT: {
    border: "1px solid #868686",
    color: "#868686",
    background: "#f3f3f3",
  },
  APPROVED: {
    border: "1px solid #008b80",
    color: "#008b80",
    background: "#bfe2df",
  },
  ORDERED: {
    border: "1px solid #007474",
    color: "#007474",
    background: "#bfdcdc",
  },
  PICKUP_DELIVERY: {
    border: "1px solid #e1a900",
    color: "#e1a900",
    background: "#f8eac0",
  },
  READY_TO_USE: {
    border: "1px solid #085e5b",
    color: "#085e5b",
    background: "#d4ebe6",
  },
  // The two terminal states have to read differently at a glance: DONE is
  // work that finished, CLOSED is work that stopped. DONE is the only solid
  // token, so the finished state stands out from every stage before it.
  DONE: {
    border: "1px solid #085e5b",
    color: "#ffffff",
    background: "#085e5b",
    accent: "#085e5b",
  },
  CLOSED: {
    border: "1px solid #4b4b4b",
    color: "#4b4b4b",
    background: "#ececec",
  },
};

type PropsType<T extends string> = {
  statuses: T[];
  colors: Record<T, StatusColors>;
  selected: T[];
  onToggle: (status: T) => void;
  // Required, not optional (EE-238): when this was optional, a page that
  // stopped passing it silently lost the select-all chip - the exact drift
  // this component exists to prevent.
  onToggleAll: () => void;
  allLabel?: string;
  // EE-253: what each status is called on screen. Without it the chips read
  // the raw enum value, which is fine for ORDERED and unreadable for
  // PICKUP_DELIVERY / AWAITING_RESPONSE.
  labels?: Record<T, string>;
};

// An unselected chip is no longer painted in its status colour. With every
// chip coloured all the time, a filter that was off looked identical to one
// that was on apart from the fill, so the row read as "everything is active".
// Off chips now render grey and only take their status colour on hover (a
// preview of what selecting them gives) and when actually selected.
//
// The states themselves are in StatusFilterChips.module.css off the palette in
// styles/global.css. All this file passes down is the one thing a stylesheet
// cannot know: which colour the chip's status carries, as `--chip-color`.
const chipColorVar = (color: string) =>
  ({ "--chip-color": color } as CSSProperties);

const StatusFilterChips = <T extends string>({
  statuses,
  colors,
  selected,
  onToggle,
  onToggleAll,
  allLabel = "All",
  labels,
}: PropsType<T>) => {
  const allSelected = statuses.length > 0 && selected.length === statuses.length;

  return (
    <div className={styles.row}>
      <Button
        size="small"
        variant="outlined"
        onClick={onToggleAll}
        className={`${styles.chip} ${styles.chipAll} ${
          allSelected ? styles.chipActive : ""
        }`}
      >
        {allLabel}
      </Button>
      {statuses.map((status) => {
        const statusColors = colors[status];
        const isActive = selected.includes(status);
        return (
          <Button
            key={status}
            size="small"
            variant="outlined"
            onClick={() => onToggle(status)}
            className={`${styles.chip} ${isActive ? styles.chipActive : ""}`}
            style={chipColorVar(statusColors.accent ?? statusColors.color)}
          >
            {labels?.[status] ?? status}
          </Button>
        );
      })}
    </div>
  );
};

export default StatusFilterChips;
